import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  StandardizedTable,
  TableColumn,
  TrendIndicator,
} from "@/components/ui/standardized-table";
import { Skeleton } from "@/components/ui/skeleton";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import type { TableQueryDTO } from "@/types/table-query";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  CarregandoSobreposto,
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import {
  MIDIA_PAGA,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";
import {
  getUtmAdContentApiV1UtmAdContentGet,
  getUtmCampaignsApiV1UtmCampaignsGet,
  getUtmCardsApiV1UtmCardsGet,
  getUtmMediumApiV1UtmMediumGet,
  getUtmSessionSourceApiV1UtmSessionSourceGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  GetUtmAdContentApiV1UtmAdContentGetData,
  GetUtmCardsApiV1UtmCardsGetData,
  UtmAdContentTableResponse,
  UtmCampaignTableResponse,
  UtmCardsResponse,
  UtmMediumTableResponse,
  UtmSourceTableResponse,
} from "@/backend-sdk/client/types.gen";
import {
  ActiveFiltersDisplay,
  DrilldownProvider,
  useDrilldown,
  useDrilldownOptional,
  type DrillDimension,
} from "@/features/drilldown";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";

// Funções de formatação
const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercentage = (value: number) => `${value.toFixed(2)}%`;

const formatNumber = (value: number) => value.toLocaleString("pt-BR");

/**
 * O que os números da UTM precisam. Tudo sai do GA4, menos o CPS, que divide o investimento
 * do Google Ads e do Meta pelas sessões do GA4 (M-108): qualquer uma das duas mídias já o produz.
 */
const FONTES: Readonly<Record<"ga4" | "cps", readonly RequisitoDeFonte[]>> = {
  ga4: ["ga4"],
  cps: ["ga4", MIDIA_PAGA],
};

const renderDeltaCell = (value: unknown): React.ReactNode => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num))
    return <span className="text-muted-foreground">—</span>;
  return <TrendIndicator value={num} />;
};

type UtmMetricValue =
  UtmCardsResponse[keyof UtmCardsResponse] extends infer Card
    ? Card extends { value: infer Value }
      ? Value
      : never
    : never;
type UtmCardsQuery = GetUtmCardsApiV1UtmCardsGetData["query"];
type UtmTableQuery = GetUtmAdContentApiV1UtmAdContentGetData["query"];
type UtmCrossFilterKey = Extract<keyof UtmCardsQuery, `filter_${string}`>;
type UtmCrossFilters = Pick<UtmCardsQuery, UtmCrossFilterKey>;

const getUtmCrossFilters = (
  filters: Record<string, string> | undefined,
  omit?: UtmCrossFilterKey,
): Partial<UtmCrossFilters> => {
  const crossFilters: Partial<UtmCrossFilters> = {
    filter_session_source: filters?.filter_session_source,
    filter_session_medium: filters?.filter_session_medium,
    filter_campaign: filters?.filter_campaign,
    filter_ad_content: filters?.filter_ad_content,
    filter_date: filters?.filter_date,
  };

  if (omit) {
    delete crossFilters[omit];
  }

  return crossFilters;
};

const getUtmTableQuery = ({
  startDate,
  endDate,
  comparison,
  customerUuid,
  accountId,
  page,
  pageSize,
  tableQuery,
  crossFilters,
}: {
  startDate: string;
  endDate: string;
  comparison: string;
  customerUuid: string;
  accountId: string | undefined;
  page: number;
  pageSize: number;
  tableQuery: TableQueryDTO;
  crossFilters: Partial<UtmCrossFilters>;
}): UtmTableQuery => ({
  start_date: startDate,
  end_date: endDate,
  comparison,
  customer_uuid: customerUuid,
  account_id: accountId,
  page,
  page_size: pageSize,
  q: tableQuery.q,
  sort_by: tableQuery.sortBy,
  sort_dir: tableQuery.sortDir,
  filters: tableQuery.filters?.length
    ? JSON.stringify(tableQuery.filters)
    : undefined,
  ...crossFilters,
});

const getUtmMetricNumber = (
  value: UtmMetricValue | number | null | undefined,
) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value) || 0;
  return 0;
};

const isMissingUtmMetric = (
  value: UtmMetricValue | number | null | undefined,
) => value === null || value === undefined;

const formatValueByType = (value: unknown, dataType: string) => {
  // Se o valor é null ou undefined, retorna N/D
  if (value === null || value === undefined)
    return <ValorIndisponivel fontes={FONTES.ga4} />;

  // Valores numéricos (incluindo 0) são válidos e devem ser formatados
  switch (dataType) {
    case "CURRENCY":
      return formatCurrency(Number(value) || 0);
    case "PERCENT":
      return formatPercentage(Number(value) || 0);
    case "NUMBER":
      return formatNumber(Number(value) || 0);
    default:
      return String(value);
  }
};

// Componente para a tabela "Origem da sessão"
const OrigemSessaoTable = ({
  campaignFiltersNonce,
}: {
  campaignFiltersNonce: number;
}) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const apiFiltersKey = drilldown?.apiFiltersKey;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<UtmSourceTableResponse | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const loadTableData = useCallback(
    async (page: number) => {
      if (!filterState) return;

      const customerUuid =
        localStorage.getItem("active_customer_uuid") ||
        selectedAccount?.customer_uuid ||
        selectedAccount?.id;
      // account_id aqui é o identificador da integração (ex.: property_id do GA4).
      // Não devemos cair em selectedAccount.id (isso é ID interno/subconta e quebra filtros).
      const accountId = selectedAccount?.account_id ?? undefined;

      if (!customerUuid) return;

      try {
        setLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);

        let comparison = "previous_period";
        if (filterState.comparison?.type === "previousYear") {
          comparison = "previous_year";
        } else if (filterState.comparison?.type === "previous") {
          comparison = "previous_period";
        }

        const { data } = await getUtmSessionSourceApiV1UtmSessionSourceGet({
          query: getUtmTableQuery({
            startDate,
            endDate,
            comparison,
            customerUuid,
            accountId,
            page,
            pageSize: 10,
            tableQuery,
            crossFilters: getUtmCrossFilters(
              drilldown?.apiFilters,
              "filter_session_source",
            ),
          }),
          throwOnError: true,
        });

        setTableData(data);
      } catch (error) {
        console.error("❌ Erro ao carregar tabela Origem da Sessão:", error);
        setTableData(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filterState,
      selectedAccount,
      tableQuery,
      apiFiltersKey,
      campaignFiltersNonce,
    ],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadTableData(currentPage);
  }, [loadTableData, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const columns: TableColumn[] = [
    {
      key: "origem",
      header: "Origem da sessão",
      align: "left",
      alwaysVisible: true,
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "usuarios_ativos",
      header: "Usuários ativos",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.usuarios_ativos_data_type),
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value, item) => formatValueByType(value, item.sessoes_data_type),
    },
    {
      key: "sessoes_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.taxa_conversao_data_type),
    },
    {
      key: "taxa_conversao_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value, item) => formatValueByType(value, item.pedidos_data_type),
    },
    {
      key: "pedidos_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.ticket_medio_data_type),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.receita_total_data_type),
    },
    {
      key: "receita_total_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
  ];

  // Skeleton apenas no primeiro carregamento (evita desmontar/remontar a tabela e causar "pisca-pisca")
  if (loading && !tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Origem da sessão</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por origem da sessão
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Origem da sessão</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por origem da sessão
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Não foi possível carregar os dados. Por favor, tente novamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <StandardizedTable
      title="Origem da sessão"
      description={
        drilldown
          ? "Clique em uma origem para filtrar a página inteira"
          : "Análise detalhada por origem da sessão"
      }
      columns={columns}
      data={tableData.items || []}
      loading={loading}
      total={tableData.total}
      clickableRows={!!drilldown}
      onRowClick={(row) => {
        if (!drilldown) return;
        drilldown.selectRow(
          "session_source",
          String(row.origem),
          String(row.origem),
        );
      }}
      selectedRowIndex={
        drilldown
          ? (tableData.items || []).findIndex((row: { origem?: unknown }) =>
              drilldown.isSelected("session_source", String(row.origem)),
            )
          : undefined
      }
      pagination={{
        current: tableData.pagination?.current_page || 1,
        pageSize: 10,
        total: tableData.pagination?.total_items || 0,
        onPageChange: handlePageChange,
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "Campanhas"
const CampanhasTable = ({
  campaignFiltersNonce,
}: {
  campaignFiltersNonce: number;
}) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const apiFiltersKey = drilldown?.apiFiltersKey;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<UtmCampaignTableResponse | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const loadTableData = useCallback(
    async (page: number) => {
      if (!filterState) return;

      const customerUuid =
        localStorage.getItem("active_customer_uuid") ||
        selectedAccount?.customer_uuid ||
        selectedAccount?.id;
      const accountId = selectedAccount?.account_id ?? undefined;

      if (!customerUuid) return;

      try {
        setLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);

        let comparison = "previous_period";
        if (filterState.comparison?.type === "previousYear") {
          comparison = "previous_year";
        } else if (filterState.comparison?.type === "previous") {
          comparison = "previous_period";
        }

        const { data } = await getUtmCampaignsApiV1UtmCampaignsGet({
          query: getUtmTableQuery({
            startDate,
            endDate,
            comparison,
            customerUuid,
            accountId,
            page,
            pageSize: 10,
            tableQuery,
            crossFilters: getUtmCrossFilters(
              drilldown?.apiFilters,
              "filter_campaign",
            ),
          }),
          throwOnError: true,
        });

        setTableData(data);
      } catch (error) {
        console.error("❌ Erro ao carregar tabela Campanhas:", error);
        setTableData(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filterState,
      selectedAccount,
      tableQuery,
      apiFiltersKey,
      campaignFiltersNonce,
    ],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadTableData(currentPage);
  }, [loadTableData, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const columns: TableColumn[] = [
    {
      key: "campanha",
      header: "Campanha da sessão",
      align: "left",
      alwaysVisible: true,
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "usuarios_ativos",
      header: "Usuários ativos",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.usuarios_ativos_data_type),
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value, item) => formatValueByType(value, item.sessoes_data_type),
    },
    {
      key: "sessoes_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.taxa_conversao_data_type),
    },
    {
      key: "taxa_conversao_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value, item) => formatValueByType(value, item.pedidos_data_type),
    },
    {
      key: "pedidos_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.ticket_medio_data_type),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.receita_total_data_type),
    },
    {
      key: "receita_total_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
  ];

  if (loading && !tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Campanhas</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por campanha
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Campanhas</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por campanha
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Não foi possível carregar os dados. Por favor, tente novamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <StandardizedTable
      title="Campanhas"
      description={
        drilldown
          ? "Clique em uma campanha para filtrar a página inteira"
          : "Análise detalhada por campanha"
      }
      columns={columns}
      data={tableData.items || []}
      loading={loading}
      total={tableData.total}
      clickableRows={!!drilldown}
      onRowClick={(row) => {
        if (!drilldown) return;
        drilldown.selectRow(
          "campaign",
          String(row.campanha),
          String(row.campanha),
        );
      }}
      selectedRowIndex={
        drilldown
          ? (tableData.items || []).findIndex((row: { campanha?: unknown }) =>
              drilldown.isSelected("campaign", String(row.campanha)),
            )
          : undefined
      }
      pagination={{
        current: tableData.pagination?.current_page || 1,
        pageSize: 10,
        total: tableData.pagination?.total_items || 0,
        onPageChange: handlePageChange,
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "Meio da sessão"
const MeioSessaoTable = ({
  campaignFiltersNonce,
}: {
  campaignFiltersNonce: number;
}) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const apiFiltersKey = drilldown?.apiFiltersKey;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<UtmMediumTableResponse | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const loadTableData = useCallback(
    async (page: number) => {
      if (!filterState) return;

      const customerUuid =
        localStorage.getItem("active_customer_uuid") ||
        selectedAccount?.customer_uuid ||
        selectedAccount?.id;
      const accountId = selectedAccount?.account_id ?? undefined;

      if (!customerUuid) return;

      try {
        setLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);

        let comparison = "previous_period";
        if (filterState.comparison?.type === "previousYear") {
          comparison = "previous_year";
        } else if (filterState.comparison?.type === "previous") {
          comparison = "previous_period";
        }

        const { data } = await getUtmMediumApiV1UtmMediumGet({
          query: getUtmTableQuery({
            startDate,
            endDate,
            comparison,
            customerUuid,
            accountId,
            page,
            pageSize: 10,
            tableQuery,
            crossFilters: getUtmCrossFilters(
              drilldown?.apiFilters,
              "filter_session_medium",
            ),
          }),
          throwOnError: true,
        });

        setTableData(data);
      } catch (error) {
        console.error("❌ Erro ao carregar tabela Meio da Sessão:", error);
        setTableData(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filterState,
      selectedAccount,
      tableQuery,
      apiFiltersKey,
      campaignFiltersNonce,
    ],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadTableData(currentPage);
  }, [loadTableData, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const columns: TableColumn[] = [
    {
      key: "meio",
      header: "Meio da sessão",
      align: "left",
      alwaysVisible: true,
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "usuarios_ativos",
      header: "Usuários ativos",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.usuarios_ativos_data_type),
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value, item) => formatValueByType(value, item.sessoes_data_type),
    },
    {
      key: "sessoes_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.taxa_conversao_data_type),
    },
    {
      key: "taxa_conversao_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value, item) => formatValueByType(value, item.pedidos_data_type),
    },
    {
      key: "pedidos_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.ticket_medio_data_type),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.receita_total_data_type),
    },
    {
      key: "receita_total_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
  ];

  if (loading && !tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Meio da sessão</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por meio da sessão
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Meio da sessão</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por meio da sessão
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Não foi possível carregar os dados. Por favor, tente novamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <StandardizedTable
      title="Meio da sessão"
      description={
        drilldown
          ? "Clique em um meio para filtrar a página inteira"
          : "Análise detalhada por meio da sessão"
      }
      columns={columns}
      data={tableData.items || []}
      loading={loading}
      total={tableData.total}
      clickableRows={!!drilldown}
      onRowClick={(row) => {
        if (!drilldown) return;
        drilldown.selectRow(
          "session_medium",
          String(row.meio),
          String(row.meio),
        );
      }}
      selectedRowIndex={
        drilldown
          ? (tableData.items || []).findIndex((row: { meio?: unknown }) =>
              drilldown.isSelected("session_medium", String(row.meio)),
            )
          : undefined
      }
      pagination={{
        current: tableData.pagination?.current_page || 1,
        pageSize: 10,
        total: tableData.pagination?.total_items || 0,
        onPageChange: handlePageChange,
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

// Componente para a tabela "Conteúdo do anúncio"
const ConteudoAnuncioTable = ({
  campaignFiltersNonce,
}: {
  campaignFiltersNonce: number;
}) => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const drilldown = useDrilldownOptional();
  const apiFiltersKey = drilldown?.apiFiltersKey;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<UtmAdContentTableResponse | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  const loadTableData = useCallback(
    async (page: number) => {
      if (!filterState) return;

      const customerUuid =
        localStorage.getItem("active_customer_uuid") ||
        selectedAccount?.customer_uuid ||
        selectedAccount?.id;
      const accountId = selectedAccount?.account_id ?? undefined;

      if (!customerUuid) return;

      try {
        setLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);

        let comparison = "previous_period";
        if (filterState.comparison?.type === "previousYear") {
          comparison = "previous_year";
        } else if (filterState.comparison?.type === "previous") {
          comparison = "previous_period";
        }

        const { data } = await getUtmAdContentApiV1UtmAdContentGet({
          query: getUtmTableQuery({
            startDate,
            endDate,
            comparison,
            customerUuid,
            accountId,
            page,
            pageSize: 10,
            tableQuery,
            crossFilters: getUtmCrossFilters(
              drilldown?.apiFilters,
              "filter_ad_content",
            ),
          }),
          throwOnError: true,
        });

        setTableData(data);
      } catch (error) {
        console.error("❌ Erro ao carregar tabela Conteúdo do Anúncio:", error);
        setTableData(null);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filterState,
      selectedAccount,
      tableQuery,
      apiFiltersKey,
      campaignFiltersNonce,
    ],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadTableData(currentPage);
  }, [loadTableData, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const columns: TableColumn[] = [
    {
      key: "conteudo",
      header: "Conteúdo do anúncio manual da sessão",
      align: "left",
      alwaysVisible: true,
      render: (value, item, index, isTotal) =>
        isTotal ? value : `${index + 1}. ${value}`,
    },
    {
      key: "usuarios_ativos",
      header: "Usuários ativos",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.usuarios_ativos_data_type),
    },
    {
      key: "sessoes",
      header: "Sessões",
      align: "right",
      render: (value, item) => formatValueByType(value, item.sessoes_data_type),
    },
    {
      key: "sessoes_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "taxa_conversao",
      header: "TX Conversão",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.taxa_conversao_data_type),
    },
    {
      key: "taxa_conversao_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "pedidos",
      header: "Pedidos",
      align: "right",
      render: (value, item) => formatValueByType(value, item.pedidos_data_type),
    },
    {
      key: "pedidos_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "ticket_medio",
      header: "TM",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.ticket_medio_data_type),
    },
    {
      key: "ticket_medio_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "receita_total",
      header: "Receita total",
      align: "right",
      render: (value, item) =>
        formatValueByType(value, item.receita_total_data_type),
    },
    {
      key: "receita_total_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
  ];

  if (loading && !tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conteúdo do anúncio</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por conteúdo do anúncio
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tableData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conteúdo do anúncio</CardTitle>
          <p className="text-sm text-muted-foreground">
            Análise detalhada por conteúdo do anúncio
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Não foi possível carregar os dados. Por favor, tente novamente.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <StandardizedTable
      title="Conteúdo do anúncio"
      description={
        drilldown
          ? "Clique em um conteúdo para filtrar a página inteira"
          : "Análise detalhada por conteúdo do anúncio"
      }
      columns={columns}
      data={tableData.items || []}
      loading={loading}
      total={tableData.total}
      clickableRows={!!drilldown}
      onRowClick={(row) => {
        if (!drilldown) return;
        drilldown.selectRow(
          "ad_content",
          String(row.conteudo),
          String(row.conteudo),
        );
      }}
      selectedRowIndex={
        drilldown
          ? (tableData.items || []).findIndex((row: { conteudo?: unknown }) =>
              drilldown.isSelected("ad_content", String(row.conteudo)),
            )
          : undefined
      }
      pagination={{
        current: tableData.pagination?.current_page || 1,
        pageSize: 10,
        total: tableData.pagination?.total_items || 0,
        onPageChange: handlePageChange,
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setCurrentPage(1);
      }}
    />
  );
};

const UTMPageContent: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();
  const { apiFilters, apiFiltersKey } = useDrilldown();
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const customerUuid =
    localStorage.getItem("active_customer_uuid") ||
    selectedAccount?.customer_uuid ||
    selectedAccount?.id ||
    null;
  const accountId = selectedAccount?.account_id ?? undefined;
  // Começa carregando: sem isto, o primeiro desenho (antes do efeito) mostrava os oito cards
  // como N/D enquanto a requisição ainda nem tinha saído.
  const [loading, setLoading] = useState(true);
  const [erroCards, setErroCards] = useState(false);
  const [cardsData, setCardsData] = useState<UtmCardsResponse | null>(null);

  const getKpiFormat = (dataType: string) => {
    switch (dataType) {
      case "CURRENCY":
        return (n: number) => formatCurrency(n);
      case "PERCENT":
        return (n: number) => formatPercentage(n);
      case "NUMBER":
      default:
        return (n: number) => formatNumber(n);
    }
  };

  const loadCardsData = useCallback(async () => {
    if (!filterState) return;

    const customerUuid =
      localStorage.getItem("active_customer_uuid") ||
      selectedAccount?.customer_uuid ||
      selectedAccount?.id;
    const accountId = selectedAccount?.account_id ?? undefined;

    if (!customerUuid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErroCards(false);
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);

      // Mapear corretamente o tipo de comparação
      let comparison = "previous_period";
      if (filterState.comparison?.type === "previousYear") {
        comparison = "previous_year";
      } else if (filterState.comparison?.type === "previous") {
        comparison = "previous_period";
      }

      const { data } = await getUtmCardsApiV1UtmCardsGet({
        query: {
          start_date: startDate,
          end_date: endDate,
          comparison,
          customer_uuid: customerUuid,
          account_id: accountId,
          ...getUtmCrossFilters(apiFilters),
        },
        throwOnError: true,
      });

      setCardsData(data);
    } catch (error) {
      console.error("❌ Erro ao carregar cards UTM:", error);
      setCardsData(null);
      setErroCards(true);
    } finally {
      setLoading(false);
    }
  }, [filterState, selectedAccount, apiFilters]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadCardsData();
  }, [loadCardsData]);

  return (
    <div className="p-6 space-y-6 page-background">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">UTM</h1>
        <p className="text-muted-foreground">
          Análise detalhada das métricas UTM e performance das campanhas
        </p>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            accountId={accountId}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />
      <ActiveFiltersDisplay emptyText="Clique em uma linha para filtrar a página inteira" />

      {/* PRIMEIRA LINHA: CARDS */}
      <div className="relative">
        {/* Overlay de loading para evitar "pisca-pisca" (mantém os KPIs visíveis e indica atualização) */}
        <CarregandoSobreposto visivel={loading && cardsData !== null} />

        <div
          className={`grid gap-4 grid-cols-2 lg:grid-cols-4 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}
        >
          {/* Receita Total */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>RECEITA TOTAL</span>
                  <HelpTooltip
                    content={getKpiTooltip("Receita Total")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.receita_total?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.receita_total?.value,
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.receita_total?.data_type ??
                                  "CURRENCY",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Receita total"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.receita_total && (
                      <TrendIndicator
                        value={cardsData.receita_total.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* CPS GERAL */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>CPS GERAL</span>
                  <HelpTooltip
                    content={getKpiTooltip("CPS GERAL")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.cps_geral?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.cps}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.cps}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.cps_geral?.value,
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.cps_geral?.data_type ?? "CURRENCY",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="CPS geral"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.cps_geral && (
                      <TrendIndicator
                        value={cardsData.cps_geral.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Sessões */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>SESSÕES</span>
                  <HelpTooltip
                    content={getKpiTooltip("Sessões")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.sessoes?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.sessoes?.value,
                            )}
                            format={getKpiFormat(
                              String(cardsData?.sessoes?.data_type ?? "NUMBER"),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Sessões"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.sessoes && (
                      <TrendIndicator
                        value={cardsData.sessoes.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Taxa Conversão */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>TAXA CONVERSÃO</span>
                  <HelpTooltip
                    content={getKpiTooltip("Taxa Conversão")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.taxa_conversao?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.taxa_conversao?.value,
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.taxa_conversao?.data_type ??
                                  "PERCENT",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Taxa de conversão"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.taxa_conversao && (
                      <TrendIndicator
                        value={cardsData.taxa_conversao.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Pedidos */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>PEDIDOS</span>
                  <HelpTooltip
                    content={getKpiTooltip("Pedidos")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.pedidos?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.pedidos?.value,
                            )}
                            format={getKpiFormat(
                              String(cardsData?.pedidos?.data_type ?? "NUMBER"),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Pedidos"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.pedidos && (
                      <TrendIndicator
                        value={cardsData.pedidos.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Ticket Médio */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>TICKET MÉDIO</span>
                  <HelpTooltip
                    content={getKpiTooltip("Ticket Médio")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(cardsData?.ticket_medio?.value) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={getUtmMetricNumber(
                              cardsData?.ticket_medio?.value,
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.ticket_medio?.data_type ??
                                  "CURRENCY",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Ticket médio"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.ticket_medio && (
                      <TrendIndicator
                        value={cardsData.ticket_medio.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Média Itens P/ Pedido */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>MÉDIA ITENS P/ PEDIDO</span>
                  <HelpTooltip
                    content={getKpiTooltip("Média Itens P/ Pedido")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(
                        cardsData?.media_itens_pedido?.value,
                      ) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={Number(
                              getUtmMetricNumber(
                                cardsData?.media_itens_pedido?.value,
                              ),
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.media_itens_pedido?.data_type ??
                                  "NUMBER",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Média de itens por pedido"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.media_itens_pedido && (
                      <TrendIndicator
                        value={cardsData.media_itens_pedido.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Valor Médio Unitário */}
          <Card className="card-kpi-primary">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span>VALOR MÉDIO UNITÁRIO</span>
                  <HelpTooltip
                    content={getKpiTooltip("Valor Médio Unitário")}
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading && !cardsData ? (
                <CarregandoValor className="text-2xl" />
              ) : (
                <>
                  <div className="flex flex-col items-start min-w-0">
                    <div className="min-w-0 flex-1">
                      {isMissingUtmMetric(
                        cardsData?.valor_medio_unitario?.value,
                      ) ? (
                        <ValorIndisponivel
                          fontes={FONTES.ga4}
                          erro={erroCards}
                        />
                      ) : (
                        <EstadoDoValor
                          carregando={false}
                          disponivel
                          fontes={FONTES.ga4}
                        >
                          <KpiNumber
                            value={Number(
                              getUtmMetricNumber(
                                cardsData?.valor_medio_unitario?.value,
                              ),
                            )}
                            format={getKpiFormat(
                              String(
                                cardsData?.valor_medio_unitario?.data_type ??
                                  "CURRENCY",
                              ),
                            )}
                            className="font-light text-foreground"
                            maxFontPx={28}
                            minFontPx={16}
                            breathingRoomPx={16}
                            ariaLabel="Valor médio unitário"
                          />
                        </EstadoDoValor>
                      )}
                    </div>
                    {cardsData?.valor_medio_unitario && (
                      <TrendIndicator
                        value={cardsData.valor_medio_unitario.change_percentage}
                      />
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SEGUNDA LINHA: TABELA ORIGEM DA SESSÃO */}
      <div className="space-y-6">
        <OrigemSessaoTable campaignFiltersNonce={campaignFiltersNonce} />
      </div>

      {/* TERCEIRA LINHA: TABELA CAMPANHAS */}
      <div className="space-y-6">
        <CampanhasTable campaignFiltersNonce={campaignFiltersNonce} />
      </div>

      {/* QUARTA LINHA: TABELA MEIO DA SESSÃO */}
      <div className="space-y-6">
        <MeioSessaoTable campaignFiltersNonce={campaignFiltersNonce} />
      </div>

      {/* QUINTA LINHA: TABELA CONTEÚDO DO ANÚNCIO */}
      <div className="space-y-6">
        <ConteudoAnuncioTable campaignFiltersNonce={campaignFiltersNonce} />
      </div>
    </div>
  );
};

const UTM_HIERARCHY: DrillDimension[] = [
  "session_source",
  "session_medium",
  "campaign",
  "ad_content",
  "date",
];

const UTMPage: React.FC = () => {
  return (
    <DrilldownProvider hierarchy={UTM_HIERARCHY}>
      <UTMPageContent />
    </DrilldownProvider>
  );
};

export default UTMPage;
